import {
  useNavigation,
  useNavigationState,
} from '../features/navigation/NavigationProvider';
import {
  CameraIcon,
  CloseIcon,
  CropIcon,
  DeleteIcon,
  ImageIcon,
  ImagePlusIcon,
} from './ui/icons';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Box,
  ButtonBase,
  CircularProgress,
  Dialog,
  IconButton,
  LinearProgress,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CameraCapture from './CameraCapture';
import { useImageInput } from '../hooks/useImageInput';
import ImageCropModal, { CropViewState } from './ImageCropModal';

export interface ImagePickerHandle {
  openFilePicker: () => void;
}

interface ImagePickerProps {
  navigationKey?: string;
  active: boolean;
  onImage: (file: File) => void;
  image?: string | null;
  compressionInProgress?: boolean;
  compressionProgress?: number;
  onClear?: () => void;
  clearing?: boolean;
  readOnly?: boolean;
}

const MIN_IMAGE_SCALE = 1;
const MAX_IMAGE_SCALE = 4;

interface Point {
  x: number;
  y: number;
}

const distanceBetween = (first: Point, second: Point) =>
  Math.hypot(second.x - first.x, second.y - first.y);

const ZoomableImage: React.FC<{ src: string; active: boolean }> = ({
  src,
  active,
}) => {
  const [scale, setScale] = useState(MIN_IMAGE_SCALE);
  const [position, setPosition] = useState<Point>({ x: 0, y: 0 });
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const pointers = useRef(new Map<number, Point>());
  const lastPanPoint = useRef<Point | null>(null);
  const lastPinchDistance = useRef<number | null>(null);

  useLayoutEffect(() => {
    if (!active) return;
    setScale(MIN_IMAGE_SCALE);
    setPosition({ x: 0, y: 0 });
    pointers.current.clear();
    lastPanPoint.current = null;
    lastPinchDistance.current = null;
  }, [active, src]);

  useEffect(() => {
    if (scale === MIN_IMAGE_SCALE) setPosition({ x: 0, y: 0 });
  }, [scale]);

  useEffect(() => {
    const element = viewerRef.current;
    if (!element || !active) return;

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();
      const step = event.deltaY < 0 ? 0.25 : -0.25;
      setScale((current) =>
        Math.min(MAX_IMAGE_SCALE, Math.max(MIN_IMAGE_SCALE, current + step)),
      );
    };

    element.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      element.removeEventListener('wheel', handleWheel);
    };
  }, [active]);

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    const point = { x: event.clientX, y: event.clientY };
    pointers.current.set(event.pointerId, point);

    if (pointers.current.size === 1) {
      lastPanPoint.current = point;
    } else if (pointers.current.size === 2) {
      const [first, second] = Array.from(pointers.current.values());
      lastPinchDistance.current = distanceBetween(first, second);
      lastPanPoint.current = null;
    }
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) return;

    const point = { x: event.clientX, y: event.clientY };
    pointers.current.set(event.pointerId, point);

    if (pointers.current.size === 2) {
      const [first, second] = Array.from(pointers.current.values());
      const pinchDistance = distanceBetween(first, second);
      if (lastPinchDistance.current && lastPinchDistance.current > 0) {
        const scaleChange = pinchDistance / lastPinchDistance.current;
        setScale((current) =>
          Math.min(
            MAX_IMAGE_SCALE,
            Math.max(MIN_IMAGE_SCALE, current * scaleChange),
          ),
        );
      }
      lastPinchDistance.current = pinchDistance;
      return;
    }

    if (scale > MIN_IMAGE_SCALE && lastPanPoint.current) {
      const deltaX = point.x - lastPanPoint.current.x;
      const deltaY = point.y - lastPanPoint.current.y;
      setPosition((current) => ({
        x: current.x + deltaX,
        y: current.y + deltaY,
      }));
    }
    lastPanPoint.current = point;
  };

  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    pointers.current.delete(event.pointerId);
    lastPinchDistance.current = null;
    const remainingPoint = Array.from(pointers.current.values())[0] ?? null;
    lastPanPoint.current = remainingPoint;
  };

  return (
    <Box
      ref={viewerRef}
      aria-label="Zoomable image viewer"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerEnd}
      onPointerCancel={handlePointerEnd}
      sx={{
        width: 'min(100vw, 100dvh)',
        height: 'min(100vw, 100dvh)',
        margin: 'auto',
        flexShrink: 0,
        overflow: 'hidden',
        touchAction: 'none',
        cursor: scale > MIN_IMAGE_SCALE ? 'grab' : 'default',
        userSelect: 'none',
      }}
    >
      <Box
        component="img"
        src={src}
        alt="Item full screen"
        draggable={false}
        sx={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})`,
          transition:
            pointers.current.size > 0 ? 'none' : 'transform 120ms ease-out',
          transformOrigin: 'center',
        }}
      />
    </Box>
  );
};

const ImagePicker = forwardRef<ImagePickerHandle, ImagePickerProps>(
  (
    {
      navigationKey = 'image-picker',
      active,
      onImage,
      image,
      compressionInProgress = false,
      compressionProgress = 0,
      onClear,
      clearing = false,
      readOnly = false,
    },
    forwardedRef,
  ) => {
    const { replaceState } = useNavigation();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [replacementOpen, setReplacementOpen] = useState(false);
    const [view, setView] = useNavigationState<
      'camera' | 'crop' | 'preview' | null
    >(navigationKey, null);
    const imageSource =
      view === 'camera' ? 'camera' : replacementOpen ? 'choose' : null;
    const setImageSource = (source: 'choose' | 'camera' | null) => {
      setReplacementOpen(source === 'choose');
      if (source === 'camera') setView('camera');
      else if (view === 'camera') setView(null);
    };
    const showFullImage = active && view === 'preview';
    const showCrop = active && view === 'crop';
    const setShowFullImage = (open: boolean) =>
      setView(open ? 'preview' : null);
    const setShowCrop = (open: boolean) => setView(open ? 'crop' : null);
    const [cropImageSource, setCropImageSource] = useState<string | null>(null);
    const [originalSource, setOriginalSource] = useState<string | null>(null);
    const originalSourceRef = useRef<string | null>(null);
    const originalBlob = useRef<Blob | undefined>(undefined);
    const pendingOriginal = useRef<{ source: string; file: File } | null>(null);
    const originalObjectUrl = useRef<string | null>(null);
    const confirmedCrop = useRef<{
      source: string;
      state: CropViewState;
    } | null>(null);

    const releaseSessionOriginal = useCallback(() => {
      confirmedCrop.current = null;
      if (originalObjectUrl.current) {
        URL.revokeObjectURL(originalObjectUrl.current);
        originalObjectUrl.current = null;
      }
      originalBlob.current = undefined;
      if (pendingOriginal.current)
        URL.revokeObjectURL(pendingOriginal.current.source);
      pendingOriginal.current = null;
      originalSourceRef.current = null;
      setOriginalSource(null);
      setCropImageSource(null);
    }, []);

    const handleIncomingImage = useCallback(
      (file: File) => {
        setReplacementOpen(false);
        if (pendingOriginal.current)
          URL.revokeObjectURL(pendingOriginal.current.source);
        const source = URL.createObjectURL(file);
        pendingOriginal.current = { source, file };
        setCropImageSource(source);
        if (view === 'camera') replaceState(navigationKey, 'crop');
        else setView('crop');
      },
      [replaceState, navigationKey, view, setView],
    );

    const imageInput = useImageInput(active && !readOnly, handleIncomingImage);

    useEffect(() => {
      // Wait for navigation to close the crop before clearing its source. Clearing
      // it during an asynchronous Back would make the missing-source effect below
      // request a second Back, potentially closing the parent editor as well.
      if (view !== 'crop') {
        if (pendingOriginal.current) {
          URL.revokeObjectURL(pendingOriginal.current.source);
          pendingOriginal.current = null;
        }
        setCropImageSource(null);
      }
    }, [view]);

    useEffect(() => {
      if (
        active &&
        ((view === 'crop' && !cropImageSource) ||
          (view === 'preview' && !image))
      ) {
        setView(null);
      }
    }, [active, view, cropImageSource, image, setView]);

    useEffect(() => {
      if (!active) {
        releaseSessionOriginal();
        return;
      }

      if (!image) {
        if (!pendingOriginal.current) releaseSessionOriginal();
        return;
      }
      if (!originalSourceRef.current) {
        originalSourceRef.current = image;
        setOriginalSource(image);
      }
    }, [active, image, releaseSessionOriginal]);

    useEffect(
      () => () => {
        if (pendingOriginal.current)
          URL.revokeObjectURL(pendingOriginal.current.source);
        if (originalObjectUrl.current)
          URL.revokeObjectURL(originalObjectUrl.current);
        originalSourceRef.current = null;
      },
      [],
    );

    useEffect(() => {
      if (!active || readOnly) setReplacementOpen(false);
    }, [active, readOnly]);

    const openFilePicker = () => fileInputRef.current?.click();
    const handlePickerClick = () => {
      if (image) setShowFullImage(true);
      else if (!readOnly) setImageSource('choose');
    };

    useImperativeHandle(
      forwardedRef,
      () => ({
        openFilePicker: () => {
          if (active && !readOnly) setReplacementOpen(true);
        },
      }),
      [active, readOnly],
    );

    return (
      <Box
        data-testid="image-picker-frame"
        sx={{
          position: 'relative',
          width: '100%',
          aspectRatio: '1 / 1',
          flexShrink: 0,
          border: imageInput.isDragging
            ? (theme) => `2px dashed ${theme.palette.primary.main}`
            : image && imageSource !== 'choose'
              ? 'none'
              : (theme) => `1px dashed ${theme.palette.divider}`,
          borderRadius: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          transition: 'background-color 0.2s ease, border-color 0.2s ease',
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          bgcolor: imageInput.isDragging
            ? (theme) => alpha(theme.palette.primary.main, 0.08)
            : 'background.default',
        }}
        onDragOver={imageInput.onDragOver}
        onDragLeave={imageInput.onDragLeave}
        onDrop={imageInput.onDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          aria-label="Choose image file"
          disabled={readOnly}
          onChange={imageInput.onFileInputChange}
          onClick={(event) => event.stopPropagation()}
          hidden
        />

        {image && imageSource !== 'choose' ? (
          <ButtonBase
            aria-label="View item image full screen"
            onClick={handlePickerClick}
            sx={{ width: '100%', height: '100%', cursor: 'zoom-in' }}
          >
            <img
              src={image}
              alt="Item"
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </ButtonBase>
        ) : readOnly ? (
          <Box
            aria-label="No item image"
            sx={{ color: 'text.secondary', textAlign: 'center' }}
          >
            <ImageIcon sx={{ fontSize: 36, color: 'primary.main' }} />
            <Typography variant="body2">No item image</Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              width: '100%',
              height: '100%',
              direction: 'ltr',
            }}
          >
            <ButtonBase
              onClick={openFilePicker}
              sx={{
                flexDirection: 'column',
                gap: 1,
                p: 2,
                color: 'text.secondary',
                borderRight: '1px dashed',
                borderColor: 'divider',
                '&:hover:not(.Mui-disabled)': { bgcolor: 'action.hover' },
                '&.Mui-focusVisible': {
                  outline: '2px solid',
                  outlineOffset: -3,
                },
              }}
            >
              <ImagePlusIcon sx={{ fontSize: 36, color: 'primary.main' }} />
              <Typography
                component="span"
                variant="body2"
                sx={{ fontWeight: 600 }}
              >
                Choose image
              </Typography>
            </ButtonBase>
            <ButtonBase
              onClick={() => setImageSource('camera')}
              sx={{
                flexDirection: 'column',
                gap: 1,
                p: 2,
                color: 'text.secondary',
                '&:hover:not(.Mui-disabled)': { bgcolor: 'action.hover' },
                '&.Mui-focusVisible': {
                  outline: '2px solid',
                  outlineOffset: -3,
                },
              }}
            >
              <CameraIcon sx={{ fontSize: 36, color: 'primary.main' }} />
              <Typography
                component="span"
                variant="body2"
                sx={{ fontWeight: 600 }}
              >
                Take photo
              </Typography>
            </ButtonBase>
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: 6,
                left: 0,
                right: 0,
                textAlign: 'center',
                color: 'text.secondary',
                pointerEvents: 'none',
                '@media (pointer: coarse)': { display: 'none' },
              }}
            >
              Or drag & drop, or paste
            </Typography>
            {image && (
              <IconButton
                aria-label="Cancel image replacement"
                onClick={() => setImageSource(null)}
                sx={{ position: 'absolute', top: 8, right: 8 }}
              >
                <CloseIcon />
              </IconButton>
            )}
          </Box>
        )}

        {image && imageSource !== 'choose' && !readOnly && (
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              display: 'flex',
              gap: 1,
            }}
          >
            <IconButton
              aria-label="Crop image"
              title="Crop image"
              onClick={(event) => {
                event.stopPropagation();
                setCropImageSource(
                  originalObjectUrl.current ||
                    originalSourceRef.current ||
                    originalSource ||
                    image ||
                    null,
                );
                setShowCrop(true);
              }}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.92)',
                color: 'primary.main',
                '&:hover:not(.Mui-disabled)': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                },
              }}
            >
              <CropIcon />
            </IconButton>
            <IconButton
              aria-label="Replace image"
              title="Replace image"
              onClick={(event) => {
                event.stopPropagation();
                setImageSource('choose');
              }}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.92)',
                color: 'primary.main',
                '&:hover:not(.Mui-disabled)': {
                  bgcolor: 'rgba(255, 255, 255, 1)',
                },
              }}
            >
              <ImagePlusIcon />
            </IconButton>
            {onClear && (
              <IconButton
                aria-label="Remove image"
                title="Remove image"
                disabled={clearing}
                onClick={(event) => {
                  event.stopPropagation();
                  releaseSessionOriginal();
                  setShowCrop(false);
                  onClear();
                }}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.92)',
                  color: 'error.main',
                  '&:hover:not(.Mui-disabled)': {
                    bgcolor: 'rgba(255, 255, 255, 1)',
                  },
                }}
              >
                {clearing ? <CircularProgress size={22} /> : <DeleteIcon />}
              </IconButton>
            )}
          </Box>
        )}

        {compressionInProgress && (
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              bgcolor: 'rgba(0, 0, 0, 0.5)',
              p: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: 'white', mb: 0.5 }}>
              Optimizing image... {Math.round(compressionProgress)}%
            </Typography>
            <LinearProgress
              variant="determinate"
              value={compressionProgress}
              sx={{
                height: 4,
                borderRadius: 2,
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                '& .MuiLinearProgress-bar': { bgcolor: 'white' },
              }}
            />
          </Box>
        )}

        <Dialog
          fullScreen
          open={active && !readOnly && imageSource === 'camera'}
          onClose={() => setImageSource(image ? 'choose' : null)}
          aria-labelledby="image-source-title"
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1,
            }}
          >
            <Typography id="image-source-title" variant="h6">
              Take photo
            </Typography>
            <IconButton
              aria-label="Close image options"
              onClick={() => setImageSource(image ? 'choose' : null)}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          {active && !readOnly && imageSource === 'camera' && (
            <CameraCapture
              onBack={() => setImageSource(image ? 'choose' : null)}
              onImage={handleIncomingImage}
            />
          )}
        </Dialog>

        <Dialog
          fullScreen
          open={showFullImage}
          onClose={() => setShowFullImage(false)}
          PaperProps={{
            'aria-label': 'Full screen item image',
            sx: {
              bgcolor: 'rgba(0, 0, 0, 0.96)',
              backgroundImage: 'none',
            },
          }}
        >
          <IconButton
            aria-label="Close full screen image"
            title="Close"
            onClick={() => setShowFullImage(false)}
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              zIndex: 1,
              color: 'white',
              bgcolor: 'rgba(0, 0, 0, 0.45)',
              '&:hover:not(.Mui-disabled)': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {image && <ZoomableImage src={image} active={showFullImage} />}
        </Dialog>

        {cropImageSource && (
          <ImageCropModal
            open={showCrop}
            imageSrc={cropImageSource}
            source={pendingOriginal.current?.file ?? originalBlob.current}
            initialState={
              confirmedCrop.current?.source === cropImageSource
                ? confirmedCrop.current.state
                : undefined
            }
            onConfirm={(blob, state) => {
              confirmedCrop.current = { source: cropImageSource, state };
              setShowCrop(false);
              if (pendingOriginal.current) {
                if (originalObjectUrl.current)
                  URL.revokeObjectURL(originalObjectUrl.current);
                originalObjectUrl.current = pendingOriginal.current.source;
                originalSourceRef.current = pendingOriginal.current.source;
                originalBlob.current = pendingOriginal.current.file;
                setOriginalSource(pendingOriginal.current.source);
                pendingOriginal.current = null;
              }
              onImage(
                blob instanceof File
                  ? blob
                  : new File([blob], 'cropped.jpg', {
                      type: blob.type || 'image/jpeg',
                    }),
              );
            }}
            onCancel={() => {
              setShowCrop(false);
            }}
          />
        )}
      </Box>
    );
  },
);

ImagePicker.displayName = 'ImagePicker';

export default ImagePicker;
